// Shared layout for protected portal routes. Sidebar + main content slot.
// Responsive: sidebar collapses to top nav on mobile (< 640px).
//
// Phase 6.1 Chunk e, updated for mobile-first (hard rule #13).
// Refactored: i18n nav labels + language toggle + shared styles.

import { ReactNode, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth, hasRoleAtLeast } from '../hooks/useAuth';
import { useLang } from '../i18n';
import { colors } from './ui/styles';
import { SessionWarning } from './SessionWarning';

interface PortalLayoutProps {
  children: ReactNode;
}

const brandLabelStyle = {
  fontFamily: 'monospace',
  fontSize: '10px',
  letterSpacing: '0.22em',
  textTransform: 'uppercase' as const,
  color: colors.cyan,
} as const;

type NavKey = 'dashboard' | 'clients' | 'commissions' | 'team' | 'training' | 'whiteLabel' | 'settings' | 'profile';

const navItems: { to: string; key: NavKey; minRole: 'client' | 'partner_owner' | 'ailys_staff' | 'ailys_admin' }[] = [
  { to: '/dashboard', key: 'dashboard', minRole: 'client' },
  { to: '/clients', key: 'clients', minRole: 'partner_owner' },
  { to: '/commissions', key: 'commissions', minRole: 'partner_owner' },
  { to: '/team', key: 'team', minRole: 'partner_owner' },
  { to: '/training', key: 'training', minRole: 'partner_owner' },
  { to: '/white-label', key: 'whiteLabel', minRole: 'partner_owner' },
  { to: '/settings', key: 'settings', minRole: 'partner_owner' },
  { to: '/profile', key: 'profile', minRole: 'partner_owner' },
];

const layoutCSS = `
  .portal-mobile-bar { display: none; }
  .portal-desktop-sidebar { display: flex !important; }

  @media (max-width: 639px) {
    .portal-mobile-bar { display: block; border-bottom: 1px solid ${colors.border}; background: ${colors.cardBgLight}; }
    .portal-desktop-sidebar { display: none !important; }
  }
`;

/* ── Notification types + hook ──────────────────────── */

interface NotifItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  read: boolean;
  created_at: string;
}

function useNotifications() {
  const [items, setItems] = useState<NotifItem[]>([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/partner/notifications', { credentials: 'include' });
      if (res.ok) {
        const data = (await res.json()) as { items: NotifItem[]; unread: number };
        setItems(data.items);
        setUnread(data.unread);
      }
    } catch { /* degrade silently */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = useCallback(async () => {
    try {
      await fetch('/api/partner/notifications', { method: 'PUT', credentials: 'include' });
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  }, []);

  return { items, unread, markAllRead, reload: load };
}

export function PortalLayout({ children }: PortalLayoutProps) {
  const auth = useAuth();
  const { t, lang, setLang } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const notif = useNotifications();

  // Close dropdown on click outside
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const visibleNavItems = useMemo(
    () => navItems.filter((item) => hasRoleAtLeast(auth.role, item.minRole)),
    [auth.role],
  );

  const toggleLang = () => setLang(lang === 'en' ? 'fr' : 'en');

  const sidebarContent = (
    <>
      <div style={{ ...brandLabelStyle, marginBottom: '4px' }}>
        {t.nav.brand}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: colors.mutedLight,
          marginBottom: '24px',
          fontFamily: 'monospace',
        }}
      >
        {auth.role ?? 'unauthenticated'}
      </div>

      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            style={({ isActive }) => ({
              display: 'block',
              padding: '10px 12px',
              fontSize: '0.9rem',
              color: isActive ? colors.cyan : colors.muted,
              background: isActive ? colors.cyanBg : 'transparent',
              borderRadius: '8px',
              textDecoration: 'none',
              border: isActive ? `1px solid ${colors.cyanBorder}` : '1px solid transparent',
            })}
          >
            {t.nav[item.key]}
          </NavLink>
        ))}
      </nav>

      <div
        style={{
          borderTop: `1px solid ${colors.border}`,
          paddingTop: '12px',
          marginTop: '12px',
        }}
      >
        {/* Language toggle */}
        <button
          onClick={toggleLang}
          style={{
            display: 'block',
            width: '100%',
            marginBottom: '10px',
            padding: '6px 0',
            background: 'transparent',
            border: `1px solid ${colors.borderLight}`,
            borderRadius: '6px',
            color: colors.muted,
            fontSize: '0.75rem',
            cursor: 'pointer',
            fontFamily: 'monospace',
            letterSpacing: '0.08em',
          }}
        >
          {lang === 'en' ? 'FR' : 'EN'}
        </button>

        {/* Notification bell */}
        <div ref={notifRef} style={{ position: 'relative', marginBottom: '10px' }}>
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '6px 0',
              background: 'transparent',
              border: `1px solid ${colors.borderLight}`,
              borderRadius: '6px',
              color: colors.muted,
              fontSize: '0.8rem',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {notif.unread > 0 && (
              <span style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: colors.cyan,
                color: colors.bg,
                fontSize: '0.6rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {notif.unread > 9 ? '9+' : notif.unread}
              </span>
            )}
          </button>

          {/* Dropdown */}
          {notifOpen && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              marginBottom: 4,
              width: 300,
              maxHeight: 360,
              overflowY: 'auto',
              borderRadius: 10,
              background: colors.cardBgDark,
              border: `1px solid ${colors.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              zIndex: 60,
              padding: '12px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.text }}>{t.notifications.title}</span>
                {notif.unread > 0 && (
                  <button
                    onClick={() => notif.markAllRead()}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.cyan,
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: 0,
                    }}
                  >
                    {t.notifications.markAllRead}
                  </button>
                )}
              </div>
              {notif.items.length === 0 ? (
                <div style={{ color: colors.mutedLight, fontSize: '0.8rem', textAlign: 'center', padding: '16px 0' }}>
                  {t.notifications.empty}
                </div>
              ) : (
                notif.items.slice(0, 10).map(n => (
                  <div
                    key={n.id}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 6,
                      marginBottom: 4,
                      background: n.read ? 'transparent' : colors.cyanBg,
                      border: n.read ? 'none' : `1px solid ${colors.cyanBorder}`,
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', fontWeight: n.read ? 400 : 600, color: colors.text }}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div style={{ fontSize: '0.7rem', color: colors.mutedLight, marginTop: 2 }}>
                        {n.body}
                      </div>
                    )}
                    <div style={{ fontSize: '0.65rem', color: colors.mutedLighter, marginTop: 2 }}>
                      {new Date(n.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: '0.8rem',
            color: '#cbd5e1',
            marginBottom: '4px',
            wordBreak: 'break-all',
          }}
        >
          {auth.email ?? t.nav.brand}
        </div>
        <a
          href="/api/auth/logout"
          style={{
            fontSize: '0.75rem',
            color: colors.mutedLight,
            textDecoration: 'underline',
          }}
        >
          {t.common.signOut}
        </a>
      </div>
    </>
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: colors.bg,
        color: colors.text,
      }}
    >
      <SessionWarning />
      <div className="portal-mobile-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
          <div style={brandLabelStyle}>
            {t.nav.brand}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Mobile notification bell */}
            <button
              onClick={() => setNotifOpen(!notifOpen)}
              style={{
                position: 'relative',
                background: 'none',
                border: `1px solid ${colors.borderLight}`,
                borderRadius: '6px',
                color: colors.muted,
                padding: '6px 10px',
                fontSize: '0.8rem',
                cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {notif.unread > 0 && (
                <span style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: colors.cyan,
                  color: colors.bg,
                  fontSize: '0.5rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {notif.unread > 9 ? '9+' : notif.unread}
                </span>
              )}
            </button>
            <button
              onClick={toggleLang}
              style={{
                background: 'none',
                border: `1px solid ${colors.borderLight}`,
                borderRadius: '6px',
                color: colors.muted,
                padding: '6px 10px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                fontFamily: 'monospace',
              }}
            >
              {lang === 'en' ? 'FR' : 'EN'}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                background: 'none',
                border: `1px solid ${colors.borderLight}`,
                borderRadius: '6px',
                color: colors.muted,
                padding: '6px 10px',
                fontSize: '0.8rem',
                cursor: 'pointer',
              }}
            >
              {mobileOpen ? t.common.close : t.common.menu}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div style={{ padding: '0 16px 16px', borderBottom: `1px solid ${colors.border}` }}>
            {sidebarContent}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flex: 1 }}>
        <aside
          className="portal-desktop-sidebar"
          style={{
            width: '220px',
            flexShrink: 0,
            borderRight: `1px solid ${colors.border}`,
            background: colors.cardBgLight,
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px 16px',
          }}
        >
          {sidebarContent}
        </aside>

        <main style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>{children}</main>
      </div>

      <style>{layoutCSS}</style>
    </div>
  );
}
